import { LitElement, html, css } from 'lit';

import {
  CSS_COLOR_WHITE,
  CSS_SPACING,
  CSS_COLOR_GREY_1,
  CSS_COLOR_GREY_2,
} from '../../../../packages/neb-styles/neb-variables';
import { getAuditShieldDashboard } from '../../../api-clients/audit-shield-dashboard';

export const ELEMENTS = {
  container: { id: 'container' },
  riskScore: { id: 'risk-score' },
  codeFrequency: { id: 'code-frequency' },
  noteCompliance: { id: 'note-compliance' },
  atRiskNotesTable: { id: 'at-risk-notes-table' },
  dashboardGrid: { id: 'dashboard-grid' },
  providerScoreboard: { id: 'provider-scoreboard' },
  providerList: { id: 'provider-list' },
  failedEncounters: { id: 'failed-encounters' },
};

function getData() {
  return getAuditShieldDashboard(global.tenantId);
}

class NebPageAuditShield extends LitElement {
  static get properties() {
    return {
      data: { type: Object },
    };
  }

  static get styles() {
    return css`
      :host {
        height: 100%;
        background: ${CSS_COLOR_WHITE};
        overflow: auto;
      }

      .container {
        padding: ${CSS_SPACING};
        background: ${CSS_COLOR_WHITE};
      }

      .risk-score-banner {
        background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
        border-radius: 12px;
        padding: 2em;
        margin-bottom: ${CSS_SPACING};
        color: white;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .risk-score-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
      }

      .risk-score-main {
        text-align: center;
        flex: 1;
        position: relative;
        padding: 0 2em;
      }

      .risk-score-main::before,
      .risk-score-main::after {
        content: '';
        position: absolute;
        top: 50%;
        width: 1px;
        height: 60%;
        background: rgba(255, 255, 255, 0.2);
        transform: translateY(-50%);
      }

      .risk-score-main::before {
        left: 0;
      }

      .risk-score-main::after {
        right: 0;
      }

      .risk-score-value {
        font-size: 4em;
        font-weight: 800;
        line-height: 1;
        margin-bottom: 0.2em;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
      }

      .risk-score-label {
        font-size: 1.4em;
        font-weight: 500;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      .risk-score-factors {
        display: flex;
        gap: 3em;
        flex: 1;
        justify-content: center;
      }

      .risk-factor {
        text-align: center;
        padding: 0 1em;
      }

      .risk-factor-value {
        font-size: 2.2em;
        font-weight: 700;
        margin-bottom: 0.2em;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
      }

      .risk-factor-label {
        font-size: 1em;
        opacity: 0.8;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .notes-audited {
        text-align: center;
        flex: 1;
      }

      .notes-audited-value {
        font-size: 2.2em;
        font-weight: 700;
        margin-bottom: 0.2em;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
      }

      .notes-audited-label {
        font-size: 1em;
        opacity: 0.8;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .dashboard-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${CSS_SPACING};
        margin-bottom: ${CSS_SPACING};
      }

      .card {
        background: ${CSS_COLOR_WHITE};
        border: 1px solid ${CSS_COLOR_GREY_2};
        border-radius: 8px;
        padding: ${CSS_SPACING};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        height: 100%;
      }

      .card-header {
        font-size: 1.2em;
        font-weight: bold;
        margin-bottom: 1em;
        color: #333;
      }

      .chart-container {
        margin: 0.5em 0 0.5em 0;
        display: flex;
        flex-direction: column;
        gap: 0.3em;
      }

      .metric-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1em;
      }

      .metric {
        text-align: center;
      }

      .metric-value {
        font-size: 1.5em;
        font-weight: bold;
      }

      .metric-label {
        font-size: 0.9em;
        color: #666;
      }

      .warning {
        color: #e74c3c;
      }

      .success {
        color: #2ecc71;
      }

      .medium {
        color: #f1c40f;
      }

      .comparison-bar {
        height: 20px;
        background: ${CSS_COLOR_GREY_1};
        border-radius: 10px;
        margin: 0.5em 0;
        position: relative;
        cursor: pointer;
      }

      .comparison-fill {
        position: absolute;
        height: 100%;
        background: #4dabf7;
        border-radius: 10px;
        transition: width 0.3s ease;
      }

      .comparison-label {
        display: flex;
        justify-content: space-between;
        font-size: 0.9em;
        margin-bottom: 0.5em;
      }

      .code-info {
        display: flex;
        flex-direction: column;
        gap: 0.1em;
        margin-bottom: 0.1em;
      }

      .code-description {
        font-size: 0.8em;
        color: #666;
      }

      .code-stats {
        display: flex;
        gap: 1em;
        font-size: 0.8em;
        color: #666;
      }

      .tooltip {
        position: absolute;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 0.5em;
        border-radius: 4px;
        font-size: 0.8em;
        z-index: 1000;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      .comparison-bar:hover .tooltip {
        opacity: 1;
      }

      .table-container {
        margin-top: ${CSS_SPACING};
        overflow-x: auto;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 1em;
      }

      th,
      td {
        padding: 0.75em;
        text-align: left;
        border-bottom: 1px solid ${CSS_COLOR_GREY_2};
      }

      th {
        background-color: ${CSS_COLOR_GREY_1};
        font-weight: bold;
      }

      tr:hover {
        background-color: ${CSS_COLOR_GREY_1};
      }

      .link {
        color: #4dabf7;
        text-decoration: none;
      }

      .link:hover {
        text-decoration: underline;
      }

      .risk-high {
        color: #ff6b6b;
      }

      .risk-medium {
        color: #ffd43b;
      }

      .risk-low {
        color: #51cf66;
      }

      .comparison-bar.industry-bar {
        background: #e0e0e0;
        position: relative;
      }
      .comparison-fill.industry-fill {
        background: #b0b0b0;
        z-index: 1;
      }
      .comparison-fill.practice-fill {
        background: #4dabf7;
        z-index: 2;
        mix-blend-mode: multiply;
      }

      .compliance-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5em;
        padding: 1em;
      }

      .compliance-item {
        background: ${CSS_COLOR_GREY_1};
        border-radius: 8px;
        padding: 1.2em;
        display: flex;
        flex-direction: column;
        gap: 1em;
      }

      .compliance-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .compliance-title {
        font-weight: bold;
        font-size: 1.1em;
        color: #333;
      }

      .compliance-value {
        font-size: 1.4em;
        font-weight: bold;
      }

      .progress-bar {
        height: 8px;
        background: ${CSS_COLOR_GREY_2};
        border-radius: 4px;
        overflow: hidden;
      }

      .progress-fill {
        height: 100%;
        transition: width 0.3s ease;
      }

      .progress-fill.success {
        background: #51cf66;
      }

      .progress-fill.warning {
        background: #ff6b6b;
      }

      .compliance-details {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.8em;
        font-size: 0.9em;
      }

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: 0.2em;
      }

      .detail-label {
        color: #666;
      }

      .detail-value {
        font-weight: bold;
        color: #333;
      }

      .provider-scoreboard {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${CSS_SPACING};
        margin-bottom: ${CSS_SPACING};
      }

      .provider-list {
        background: ${CSS_COLOR_WHITE};
        border: 1px solid ${CSS_COLOR_GREY_2};
        border-radius: 8px;
        padding: 1.5em;
      }

      .provider-list-header {
        font-size: 1.2em;
        font-weight: bold;
        margin-bottom: 1em;
        color: #333;
        display: flex;
        align-items: center;
        gap: 0.5em;
      }

      .provider-list-header.top {
        color: #2ecc71;
      }

      .provider-list-header.bottom {
        color: #e74c3c;
      }

      .provider-item {
        display: flex;
        align-items: center;
        padding: 1em;
        border-bottom: 1px solid ${CSS_COLOR_GREY_2};
      }

      .provider-item:last-child {
        border-bottom: none;
      }

      .provider-rank {
        font-size: 1.5em;
        font-weight: bold;
        width: 40px;
        text-align: center;
        color: #666;
      }

      .provider-info {
        flex: 1;
      }

      .provider-name {
        font-weight: bold;
        margin-bottom: 0.2em;
      }

      .provider-stats {
        display: flex;
        gap: 1.5em;
        font-size: 0.9em;
        color: #666;
      }

      .provider-compliance {
        font-weight: bold;
      }

      .provider-compliance.high {
        color: #2ecc71;
      }

      .provider-compliance.medium {
        color: #f1c40f;
      }

      .provider-compliance.low {
        color: #e74c3c;
      }
    `;
  }

  __getRiskLevel(score) {
    if (score >= 80) return { text: 'Low Risk', class: 'success' };
    if (score >= 60) return { text: 'Medium Risk', class: 'medium' };
    return { text: 'High Risk', class: 'warning' };
  }

  async firstUpdated() {
    this.data = await getData();
  }

  // eslint-disable-next-line complexity
  render() {
    if (!this.data) return html`<div>Loading...</div>`;

    const riskLevel = this.__getRiskLevel(this.data.riskScore.overall);
    const filteredEncounters = this.data.encounters.filter(
      encounter => encounter.auditShieldPass === 0,
    );
    const notesAuditedPercentage = 75; // Mocked percentage of notes audited

    // Calculate compliance percentages for each rule
    const totalEncounters = this.data.encounters.length;

    // Calculate Subjective-Objective Compliance
    const subjectiveObjectiveFailures = this.data.encounters.filter(encounter =>
      encounter.rule.includes('Subjective-Objective'),
    ).length;
    const subjectiveObjectiveCompliance = Math.round(
      ((totalEncounters - subjectiveObjectiveFailures) / totalEncounters) * 100,
    );

    // Calculate Procedure Code Support Compliance
    const procedureSupportFailures = this.data.encounters.filter(encounter =>
      encounter.rule.includes('Procedure Code Support'),
    ).length;
    const procedureSupportCompliance = Math.round(
      ((totalEncounters - procedureSupportFailures) / totalEncounters) * 100,
    );

    // Calculate Valid Supporting Diagnosis Compliance
    const diagnosisFailures = this.data.encounters.filter(encounter =>
      encounter.rule.includes('Valid Supporting Diagnosis'),
    ).length;
    const diagnosisCompliance = Math.round(
      ((totalEncounters - diagnosisFailures) / totalEncounters) * 100,
    );

    return html`
      <div id="${ELEMENTS.container.id}" class="container">
        <h1>Risk Assessment Dashboard</h1>

        <!-- Risk Score Banner -->
        <div id="${ELEMENTS.riskScore.id}" class="risk-score-banner">
          <div class="risk-score-content">
            <div class="risk-score-factors">
              <div class="risk-factor">
                <div class="risk-factor-value">
                  ${this.data.riskScore.factors.codeFrequency}
                </div>
                <div class="risk-factor-label">Code Frequency</div>
              </div>
              <div class="risk-factor">
                <div class="risk-factor-value">
                  ${this.data.riskScore.factors.noteCompliance}
                </div>
                <div class="risk-factor-label">Note Compliance</div>
              </div>
            </div>
            <div class="risk-score-main">
              <div class="risk-score-value ${riskLevel.class}">
                ${this.data.riskScore.overall}
              </div>
              <div class="risk-score-label ${riskLevel.class}">
                ${riskLevel.text}
              </div>
            </div>
            <div class="notes-audited">
              <div class="notes-audited-value">${notesAuditedPercentage}%</div>
              <div class="notes-audited-label">Notes Audited</div>
            </div>
          </div>
        </div>

        <!-- Provider Scoreboard -->
        <div id="${ELEMENTS.providerScoreboard.id}" class="provider-scoreboard">
          <div class="provider-list">
            <div class="provider-list-header top">
              <span>🏆</span>
              Top Performing Providers
            </div>
            ${this.data.providers.slice(0, 3).map(
              (provider, index) => html`
                <div class="provider-item">
                  <div class="provider-rank">#${index + 1}</div>
                  <div class="provider-info">
                    <div class="provider-name">${provider.name}</div>
                    <div class="provider-stats">
                      <span class="provider-compliance high"
                        >${provider.compliance}% Compliance</span
                      >
                      <span>${provider.encounters} Encounters</span>
                      <span>${provider.riskLevel} Risk</span>
                    </div>
                  </div>
                </div>
              `,
            )}
          </div>

          <div id="${ELEMENTS.providerList.id}" class="provider-list">
            <div class="provider-list-header bottom">
              <span>⚠️</span>
              Providers Needing Attention
            </div>
            ${this.data.providers.slice(3).map(
              (provider, index) => html`
                <div class="provider-item">
                  <div class="provider-rank">#${index + 4}</div>
                  <div class="provider-info">
                    <div class="provider-name">${provider.name}</div>
                    <div class="provider-stats">
                      <span class="provider-compliance low"
                        >${provider.compliance}% Compliance</span
                      >
                      <span>${provider.encounters} Encounters</span>
                      <span>${provider.riskLevel} Risk</span>
                    </div>
                  </div>
                </div>
              `,
            )}
          </div>
        </div>

        <div id="${ELEMENTS.dashboardGrid.id}" class="dashboard-grid">
          <!-- Code Frequency -->
          <div class="card">
            <div id="${ELEMENTS.codeFrequency.id}" class="card-header">
              Code Frequency Analysis
            </div>
            <div class="chart-container">
              ${Object.entries(this.data.codeFrequency.practice).map(
                ([code, practiceData]) => {
                  const industryData = this.data.codeFrequency.industry[code];
                  const warnIfOver = ['98941', '98942', '98943'];
                  const isWarning =
                    warnIfOver.includes(code) &&
                    practiceData.percentage > industryData.percentage + 5;
                  return html`
                    <div class="code-info">
                      <div class="comparison-label">
                        <span>Code ${code}</span>
                        <div class="code-stats">
                          <span>Practice: ${practiceData.percentage}%</span>
                          <span>Industry: ${industryData.percentage}%</span>
                          ${isWarning
                            ? html`<span
                                style="color: #ff6b6b; font-weight: bold;"
                                >⚠️ High vs Industry</span
                              >`
                            : ''}
                        </div>
                      </div>
                      <div
                        class="comparison-bar industry-bar"
                        style="background: #e0e0e0;"
                      >
                        <div
                          class="comparison-fill industry-fill"
                          style="width: ${industryData.percentage}%; background: #b0b0b0;"
                        ></div>
                        <div
                          class="comparison-fill practice-fill"
                          style="width: ${practiceData.percentage}%; background: #4dabf7; mix-blend-mode: multiply;"
                        ></div>
                        <div class="tooltip">${practiceData.description}</div>
                      </div>
                    </div>
                  `;
                },
              )}
            </div>
          </div>

          <!-- Note Compliance -->
          <div class="card">
            <div id="${ELEMENTS.noteCompliance.id}" class="card-header">
              Note Compliance Analysis
            </div>
            <div class="compliance-grid">
              <div class="compliance-item">
                <div class="compliance-header">
                  <div class="compliance-title">
                    Subjective-Objective Compliance
                  </div>
                  <div
                    class="compliance-value ${subjectiveObjectiveCompliance >=
                    80
                      ? 'success'
                      : 'warning'}"
                  >
                    ${subjectiveObjectiveCompliance}%
                  </div>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill ${subjectiveObjectiveCompliance >= 80
                      ? 'success'
                      : 'warning'}"
                    style="width: ${subjectiveObjectiveCompliance}%"
                  ></div>
                </div>
                <div class="compliance-details">
                  <div class="detail-item">
                    <span class="detail-label">Passing Encounters:</span>
                    <span class="detail-value"
                      >${totalEncounters - subjectiveObjectiveFailures}</span
                    >
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Total Encounters:</span>
                    <span class="detail-value">${totalEncounters}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Failed Rules:</span>
                    <span class="detail-value"
                      >${subjectiveObjectiveFailures}</span
                    >
                  </div>
                </div>
              </div>

              <div class="compliance-item">
                <div class="compliance-header">
                  <div class="compliance-title">Procedure Code Support</div>
                  <div
                    class="compliance-value ${procedureSupportCompliance >= 80
                      ? 'success'
                      : 'warning'}"
                  >
                    ${procedureSupportCompliance}%
                  </div>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill ${procedureSupportCompliance >= 80
                      ? 'success'
                      : 'warning'}"
                    style="width: ${procedureSupportCompliance}%"
                  ></div>
                </div>
                <div class="compliance-details">
                  <div class="detail-item">
                    <span class="detail-label">Passing Encounters:</span>
                    <span class="detail-value"
                      >${totalEncounters - procedureSupportFailures}</span
                    >
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Total Encounters:</span>
                    <span class="detail-value">${totalEncounters}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Failed Rules:</span>
                    <span class="detail-value"
                      >${procedureSupportFailures}</span
                    >
                  </div>
                </div>
              </div>

              <div class="compliance-item">
                <div class="compliance-header">
                  <div class="compliance-title">Valid Supporting Diagnosis</div>
                  <div
                    class="compliance-value ${diagnosisCompliance >= 80
                      ? 'success'
                      : 'warning'}"
                  >
                    ${diagnosisCompliance}%
                  </div>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill ${diagnosisCompliance >= 80
                      ? 'success'
                      : 'warning'}"
                    style="width: ${diagnosisCompliance}%"
                  ></div>
                </div>
                <div class="compliance-details">
                  <div class="detail-item">
                    <span class="detail-label">Passing Encounters:</span>
                    <span class="detail-value"
                      >${totalEncounters - diagnosisFailures}</span
                    >
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Total Encounters:</span>
                    <span class="detail-value">${totalEncounters}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Failed Rules:</span>
                    <span class="detail-value">${diagnosisFailures}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Failed Encounters Table -->
        <div class="card">
          <div id="${ELEMENTS.failedEncounters.id}" class="card-header">
            Failed Encounters
          </div>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Code</th>
                  <th>Failed Rules</th>
                  <th>Reason</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                ${filteredEncounters.map(
                  encounter => html`
                    <tr>
                      <td>${encounter.dateOfService}</td>
                      <td>${encounter.patientName}</td>
                      <td>${encounter.code}</td>
                      <td>${encounter.rule.join(', ')}</td>
                      <td>${encounter.reason}</td>
                      <td>
                        <a
                          href="http://localhost:8082/practice/#/charting/${encounter.encounterId}"
                          class="link"
                          >View Encounter</a
                        >
                      </td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('neb-page-audit-shield', NebPageAuditShield);
